import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faUsers, 
  faSpinner, 
  faSearch,
  faFilter,
  faArchive,
  faEye,
  faEdit,
  faUser,
  faCalendarAlt,
  faUserTag,
  faExclamationTriangle,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';

const Controllers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [controllers, setControllers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formationFilter, setFormationFilter] = useState('all');
  const [formations, setFormations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Handle success message from navigation state
  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
      // Clear the success message after 5 seconds
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when debounced search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchFormations();
  }, []);

  useEffect(() => {
    const isSearch = debouncedSearchTerm !== '' || statusFilter !== 'all' || formationFilter !== 'all';
    fetchControllers(isSearch);
  }, [currentPage, debouncedSearchTerm, statusFilter, formationFilter]);

  const fetchControllers = async (isSearch = false) => {
    try {
      if (isSearch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      
      // If formation filter is active, load all controllers (no pagination) to filter properly
      const usePagination = formationFilter === 'all';
      
      const params = new URLSearchParams({
        scope: 'management',
        role: 'controleur' // Filter for controleur role only
      });
      
      // Only use pagination if no formation filter is active
      if (usePagination) {
        params.append('page', currentPage);
        params.append('per_page', 10);
      }
      // If formation filter is active, don't add pagination params to get all controllers
      
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const url = `/controllers?${params.toString()}`;
      const response = await api.get(url);
      
      // Extract data based on response structure
      let controllersData = [];
      if (response.data.success && Array.isArray(response.data.data)) {
        controllersData = response.data.data;
      } else if (Array.isArray(response.data.data)) {
        controllersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        controllersData = response.data;
      }
      
      // Apply formation filter on client side
      if (formationFilter !== 'all') {
        const formationId = parseInt(formationFilter);
        controllersData = controllersData.filter(controller => {
          if (!controller.completed_formation_ids || !Array.isArray(controller.completed_formation_ids)) {
            return false;
          }
          return controller.completed_formation_ids.some(f => 
            parseInt(f) === formationId || f === formationId
          );
        });
      }
      
      setControllers(controllersData);
      
      if (usePagination) {
      setTotalPages(response.data.last_page || 1);
        setTotalCount(response.data.total || controllersData.length);
      } else {
        // When filtering by formation, show all filtered results
        setTotalPages(1);
        setTotalCount(controllersData.length);
      }
    } catch (error) {
      console.error('Error fetching controllers:', error);
      setError('Erreur lors du chargement des contrôleurs');
    } finally {
      if (isSearch) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleFormationFilter = (formation) => {
    setFormationFilter(formation);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const fetchFormations = async () => {
    try {
      const response = await api.get('/formations?is_active=true');
      if (response.data.success) {
        setFormations(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching formations:', error);
      setFormations([]);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FontAwesomeIcon icon={faCircleCheck} className="text-green-500" />;
      case 'suspended':
        return <FontAwesomeIcon icon={faCircleXmark} className="text-red-500" />;
      case 'inactive':
        return <FontAwesomeIcon icon={faCircleExclamation} className="text-yellow-500" />;
      default:
        return <FontAwesomeIcon icon={faCircleXmark} className="text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'suspended':
        return 'Suspendu';
      case 'inactive':
        return 'Inactif (Automatique)';
      default:
        return 'Inconnu';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des contrôleurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-800">CONTRÔLEURS</h1>
          </div>
          {(currentUser?.role === 'resp_controle' || currentUser?.role === 'admin_it') && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button 
                onClick={() => navigate('/controllers/create')}
                className="px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Nouveau Contrôleur
              </button>
              <button 
                onClick={() => navigate('/controllers/archived')}
                className="px-6 py-3 bg-gray-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0 flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faArchive} className="mr-2" />
                Voir les Archivés
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {successMessage}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
              <span className="font-medium text-gray-700">Filtres:</span>
            </div>
            
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actif</option>
              <option value="suspended">Suspendu</option>
              <option value="inactive">Inactif</option>
            </select>

            {/* Formation Filter */}
            <select
              value={formationFilter}
              onChange={(e) => handleFormationFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none flex-shrink-0 w-full sm:w-auto"
            >
              <option value="all">Toutes les formations</option>
              {formations.map((formation) => (
                <option key={formation.id} value={formation.id}>
                  {formation.title}
                </option>
              ))}
            </select>

            {/* Search */}
            <div className="relative flex-1 w-full sm:min-w-0 min-w-0">
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none w-full pr-10 max-w-full"
              />
              {searchLoading && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <FontAwesomeIcon 
                    icon={faSpinner} 
                    className="animate-spin text-red-500 w-4 h-4" 
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Controllers Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {controllers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faUsers} className="text-4xl text-gray-300 mb-4" />
              <p className="text-lg">Aucun contrôleur trouvé</p>
              <p className="text-sm">Les contrôleurs apparaîtront ici</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contrôleur
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Spécialisation
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dernière connexion
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {controllers.map((controller) => (
                    <tr 
                      key={controller.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/controllers/${controller.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="text-red-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {controller.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {controller.ref}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {controller.email}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {controller.specialization || '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(controller.status)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getStatusText(controller.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {controller.last_login ? (
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                            {new Date(controller.last_login).toLocaleDateString('fr-FR')}
                          </div>
                        ) : (
                          <span className="text-gray-400">Jamais connecté</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
            <div className="flex items-center text-sm text-gray-700">
                <span>
                  Affichage de {((currentPage - 1) * 10) + 1} à {Math.min(currentPage * 10, totalCount)} sur {totalCount} contrôleurs
                </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
              </button>
              
              <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-red-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    );
};

export default Controllers;
